import { effect, inject, Injectable, signal } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { DIALOG_TYPES } from "../constants";
import { API_ENDPOINT } from "../constants/api-endpoint.constants";
import { SharedDialogComponent } from "../modules/common-components/shared-dialog/shared-dialog.component";
import { IGenericApi, IToast, IUpload } from "../modules/shared/shared.model";
import { ApiService } from "./api.service";
import { ClientService } from "./client.service";
import { ToastService } from "./toast.service";
import { WaitErrorDialogsService } from "./wait-error-dialogs.service";

@Injectable({
  providedIn: 'root'
})
export class FilesService {
  private readonly toast = inject(ToastService);
  private readonly apiService = inject(ApiService);
  private readonly clientService = inject(ClientService);
  private readonly weds = inject(WaitErrorDialogsService);

  private uploadFilesConfig = signal<IUpload>(null);
  private preActionConfig = signal<Partial<IGenericApi>>(null);
  private uploadActionConfig = signal<Partial<IGenericApi>>(null);
  private subsequentActionConfig = signal<Partial<IGenericApi>>(null);

  private uploadLoader: MatDialogRef<SharedDialogComponent, any>;

  private formData: FormData = new FormData();

  private preActionApi = this.apiService.apiSignal<Record<string, any>>(() => {
    const config = this.preActionConfig();
    return config ? {
      url: config.endpoint || API_ENDPOINT.GET_UPLOAD_CONFIG,
      body: config.payload || {}
    } : undefined;
  });

  private uploadFilesApi = this.apiService.apiSignal<Record<string, any>>(() => {
    const config = this.uploadActionConfig();
    return config ? {
      url: config.endpoint || 'files/upload',
      body: config.payload,
    } : undefined;
  });

  private subsequentActionApi = this.apiService.apiSignal<Record<string, any>>(() => {
    const value = this.uploadFilesApi.value();
    const config = this.subsequentActionConfig();
    if(!config) return undefined;

    let body = typeof config?.payload === 'function' ? config.payload(this.preActionApi.value()?.type) : config.payload;
    body = {
        ...(body ?? {}),
        file: {
          loc: value.f ?? value.files[0]?.loc,
          ...this.transformUploadedFilesResponse()
        }
      }
    return {
      url: config.endpoint || API_ENDPOINT.SAVE_INTEGRATIONS,
      body
    };
  });


  // ------------------------
  // Constructor
  // ------------------------

  constructor() {

    //Handles pre action API success
    effect(() => {
      const preActionResponse = this.preActionApi.hasValue() ? this.preActionApi.value() : undefined;
      const preActionError = this.preActionApi.error();

      if (preActionError) {
        this.handleApiError(preActionError);
      } else if (preActionResponse) {
        if (preActionResponse['type'] === 'S3'|| preActionResponse?.ok) {
          this.formData.append('p', preActionResponse['fileName']);
          this.formData.append('b', preActionResponse['bucket']);
          this.uploadActionConfig.update(config => ({...(config ?? {}), payload: this.formData }));
        } else {
          this.formData.append('clientId', this.clientService.clientId());
          this.uploadActionConfig.set({ endpoint: 'uploads', payload: this.formData });
        }
      }
    });

    // Handle upload files API success
    effect(() => {
      const uploadFilesResponse = this.uploadFilesApi.hasValue() ? this.uploadFilesApi.value() : undefined;
      const uploadFilesError = this.uploadFilesApi.error();

      if (uploadFilesError) {
        this.handleApiError(uploadFilesError);
      } else if (uploadFilesResponse) {
        if (this.uploadFilesConfig()?.subsequentAction) {
          this.subsequentActionConfig.set({...this.uploadFilesConfig()?.subsequentAction });
        } else {
          this.handleUploadSuccess(uploadFilesResponse);
        }
      }
    });

    // Handle subsequent action API success
    effect(() => {
      const subsequentActionResponse = this.subsequentActionApi.hasValue() ? this.subsequentActionApi.value() : undefined;
      const subsequentActionError = this.subsequentActionApi.error();

      if (subsequentActionError) {
        this.handleApiError(subsequentActionError);
      } else if (subsequentActionResponse) {
        this.handleUploadSuccess(subsequentActionResponse);
      }
    });
  }

  // ------------------------
  // Public Methods
  // ------------------------

  public uploadFiles(config: IUpload) {
    this.uploadFilesConfig.set(config);
    this.formData = new FormData();
    const files = config.files;

    for (let i = 0; i < files.length; i++) {
      let f = files[i];
      this.formData.append(config.fileKey ?? 'f', f);
    }

    for (let i = 0; i < Object.keys(config.uploadAction?.payload ?? {}).length; i++) {
      let key = Object.keys(config.uploadAction.payload)[i];
      this.formData.append(key, config.uploadAction.payload[key]);
    }

    if (config.preAction) {
      this.preActionConfig.set({...config.preAction });
    } else {
      this.uploadActionConfig.set({...(config?.uploadAction ?? {}), payload: this.formData });
    }

    if (!config.hideLoader) {
      this.uploadLoader = this.weds.showDialog({ type: DIALOG_TYPES.WAIT, title: 'Uploading File', message: 'Please hold on while we upload the file', dontCloseAllDialogs: true })
    }
  }

  public transformUploadedFilesResponse() {
    const file = this.uploadFilesConfig().files[0];
    return {
      name: this.preActionApi.value()?.type === 'S3' ? this.preActionApi.value()?.fileName : file.name?.toLowerCase(),
      size: file.size,
      mime: file.type,
      ext: file.name.split('.').pop(),
      dt: new Date(file.lastModified).toISOString()
    };
  }

  public downloadFile(href: string| Blob, name: string = '', toastConfig?: IToast) {
    if (href instanceof Blob) {
      const blobUrl = URL.createObjectURL(href);
      this.triggerFileDownload(blobUrl, name, toastConfig);
      return;
    }

    name = name || this.extractFilenameFromUrl(href);
    const hrefPatterns = ['data:text/csv;base64', 'X-Amz-Algorithm'];

    if (hrefPatterns.some(pattern => href.includes(pattern))) {
      this.triggerFileDownload(href, name, toastConfig);
      return;
    }

    this.fetchFileAsObjectUrl(href, (blobUrl, fileName) => {
      this.triggerFileDownload(blobUrl, name || fileName || '', toastConfig);
    });
  }

  public isFileSizeExceedingMaxLimit(files: Array<File> | FileList, maxFileSizeInMB: number): boolean {
    return Array.from(files).some(file => file.size > maxFileSizeInMB * 1024 * 1024);
  }

  public isValidFileFormat(fileName: string, validFormat: string) {
    //eg: fileName -> 'document.docx', validFormat -> '.doc, .docx'
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    const validFormatArray = validFormat.split(',').map((element) => element.trim());
    if (!validFormatArray.includes(extension)) {
      this.toast.showToast({
        'type': DIALOG_TYPES.ERROR, 'msg': 'Invalid file format.',
        'desc': `Only ${validFormat} files are allowed`
      });
      return false;
    }
    return true;
  }

  // ------------------------
  // Private Methods
  // ------------------------

  private handleApiError(apiError: Record<string, any>) {
    if (this.uploadFilesConfig()?.error) {
      this.uploadFilesConfig()?.error(apiError);
    } else {
      let desc = 'An error occurred while uploading the file. Please try again.';

      if (apiError.error?.error?.validationErrors?.length) {
        desc = apiError.error.error.validationErrors.map((validationError: any) => validationError.field + ': ' + validationError.message).join('\n');
      } else if (apiError.error?.error?.message) {
        desc = apiError.error.error.message;
      }
      
      this.toast.showToast({ type: DIALOG_TYPES.ERROR, msg: 'File upload failed', desc });
    }

    this.onComplete(false);
  }

  private extractFilenameFromUrl(url: string): string {
    if (typeof url === 'string' && url.includes(':::')) {
      url = this.getFilenameFromKeyBucketPair(url);
    }
    try {
      const pathname = new URL(url).pathname;
      const filename = pathname.split('/').pop() || '';
 
      // Remove query params and fragments
      return decodeURIComponent(filename.split('?')[0].split('#')[0]);
    } catch {
      return url.split('/').pop()?.split('?')[0] || 'download';
    }
  }
  
  public getFilenameFromKeyBucketPair(filePath: string): string {
    return filePath.split(':::')[0];
  }

  private triggerFileDownload(href: string, name: string = '', toastConfig?: IToast) {
    const link = document.createElement('a');
    link.href = href;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    
    if(toastConfig) this.toast.showToast(toastConfig);
  }

  private handleUploadSuccess(value: any) {
    if(this.uploadFilesConfig()?.success) {
      this.uploadFilesConfig()?.success(value);
    } else {
      this.toast.showToast({ type: DIALOG_TYPES.SUCCESS, msg: 'File uploaded successfully' });
    }
      
    this.onComplete(true);
  }

  /** Fetches a file by path and passes an object URL (and optional fileName) to the callback. Used for display (e.g. images) or by downloadFile. */
  public fetchFileAsObjectUrl(
    filePath: string,
    callback: (objectUrl: string, fileName?: string) => void,
    errorCallback?: (err: any) => void
  ): void {
    const payload = filePath.includes(':::')
      ? { url: 'files/download', body: { f: filePath } }
      : { url: filePath, method: 'GET', withCredentials: true };
    this.apiService.apiSignalOnce(
      payload,
      (resp: { file: Blob; fileName: string }) => {
        const contentType = resp.file?.type || 'application/octet-stream';
        const blob = new Blob([resp.file], { type: contentType });
        callback(URL.createObjectURL(blob), resp.fileName);
      },
      errorCallback,
      undefined,
      'blob'
    );
  }

  private clearSignals() {
    this.uploadFilesConfig.set(undefined);
    this.preActionConfig.set(undefined);
    this.uploadActionConfig.set(undefined);
    this.subsequentActionConfig.set(undefined);

    this.preActionApi.set(undefined);
    this.uploadFilesApi.set(undefined);
    this.subsequentActionApi.set(undefined);

    this.formData = new FormData();
  }

  private onComplete(success: boolean): void {
    const config = this.uploadFilesConfig();
    
    if (config?.complete) config.complete(success);
    if (!config?.hideLoader && this.uploadLoader) this.weds.closeDialog(this.uploadLoader);

    this.clearSignals();
  }
}
